import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from './ui/use-toast';
import { Loader2, AlertCircle } from 'lucide-react';
import { API_URL } from '../config/api';

interface AddSiteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSiteAdded: () => void;
}

interface SiteFormData {
  url: string;
  name: string;
  site_type: 'discovery' | 'paid' | 'trial' | 'demo';
  role_description: string;
  category: string;
  features: {
    analytics: boolean;
    chatbot: boolean;
    voicebot: boolean;
    suggestions: boolean;
    benchmarking: boolean;
    counter: boolean;
  };
}

export default function AddSiteDialog({ isOpen, onClose, onSiteAdded }: AddSiteDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SiteFormData>({
    url: '',
    name: '',
    site_type: 'discovery',
    role_description: '',
    category: '',
    features: {
      analytics: true,
      chatbot: false,
      voicebot: false,
      suggestions: true,
      benchmarking: true,
      counter: true,
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate URL
    try {
      new URL(formData.url);
    } catch {
      toast({
        title: 'Invalid URL',
        description: 'Please enter a valid URL including the protocol (https://)',
        variant: 'destructive',
      });
      return;
    }


    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({
          ...formData,
          status: 'pending', // New sites start as pending
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create site');
      }

      const result = await response.json();
      
      toast({
        title: 'Site created successfully',
        description: `${formData.name} has been added to your sites.`,
      });

      // Show warning about analytics services
      if (formData.features.analytics) {
        toast({
          title: 'Analytics Setup Required',
          description: 'Remember to configure Matomo, OpenReplay, and Snowplow for this site to enable full analytics functionality.',
          duration: 10000,
        });
      }

      onSiteAdded();
      onClose();
      
      // Reset form
      setFormData({
        url: '',
        name: '',
        site_type: 'discovery',
        role_description: '',
        category: '',
        features: {
          analytics: true,
          chatbot: false,
          voicebot: false,
          suggestions: true,
          benchmarking: true,
          counter: true,
        },
      });
    } catch (error) {
      console.error('Error creating site:', error);
      toast({
        title: 'Failed to create site',
        description: error instanceof Error ? error.message : 'An error occurred while creating the site',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add New Site</DialogTitle>
            <DialogDescription>
              Add a new site to PageMind. The site will be created in pending status.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This will only create the site record. Analytics services (Matomo, OpenReplay, Snowplow) 
                need to be configured separately to avoid losing historical data.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="url">Site URL</Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Site Name</Label>
              <Input
                id="name"
                placeholder="My Awesome Site"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>


            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="site_type">Site Type</Label>
                <Select
                  value={formData.site_type}
                  onValueChange={(value: any) => setFormData({ ...formData, site_type: value })}
                >
                  <SelectTrigger id="site_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="demo">Demo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  placeholder="e.g., E-commerce, SaaS, Blog"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role_description">Role Description</Label>
              <Textarea
                id="role_description"
                placeholder="Describe the primary role and purpose of this site..."
                value={formData.role_description}
                onChange={(e) => setFormData({ ...formData, role_description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Features</Label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="analytics" className="font-normal">Analytics</Label>
                  <Switch
                    id="analytics"
                    checked={formData.features.analytics}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, analytics: checked } })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="chatbot" className="font-normal">Chatbot</Label>
                  <Switch
                    id="chatbot"
                    checked={formData.features.chatbot}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, chatbot: checked } })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="voicebot" className="font-normal">Voicebot</Label>
                  <Switch
                    id="voicebot"
                    checked={formData.features.voicebot}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, voicebot: checked } })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="suggestions" className="font-normal">Suggestions</Label>
                  <Switch
                    id="suggestions"
                    checked={formData.features.suggestions}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, suggestions: checked } })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="benchmarking" className="font-normal">Benchmarking</Label>
                  <Switch
                    id="benchmarking"
                    checked={formData.features.benchmarking}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, benchmarking: checked } })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="counter" className="font-normal">Counter</Label>
                  <Switch
                    id="counter"
                    checked={formData.features.counter}
                    onCheckedChange={(checked) => 
                      setFormData({ ...formData, features: { ...formData.features, counter: checked } })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Site'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}